// The pieces Between parties and Profit share share (UI rebuild batch 3, area p2p) — Astra's form anatomy for both:
//
//   card   the fields in one column on the phone, related pairs side by side; "More" folds the optional ones; the foot
//          is a muted line and the one gold primary ("Review …"). Once reviewed, the foot is the review — the read-back
//          and the confirm that posts.
//   aside  laptop only: the Last 10 (Between parties) or the party's balance (Profit share), and "About this job" behind
//          a tap — the explanation, the desk and when the balances were read (deskFacts). On the phone the same fold sits
//          under the card.
//
// Presentation only. Every money rule stays in the two forms (copied from screens/RecordBetweenParties.tsx and
// screens/RecordProfitShare.tsx).
import type { ReactNode } from 'react';
import { displayAsset, fmtTime } from '../../../../lib/format';

/** The form card beside its aside: one column on the phone; from lg the card (Astra's 600 px at most) with the aside
 *  at its right, or under it when the page column is too narrow for both (the card never squeezes below 480). */
export function FormLayout({ card, aside }: { card: ReactNode; aside: ReactNode }) {
  return (
    <div className="flex flex-col gap-6 xl:flex-row xl:items-start">
      <section className="min-w-0 space-y-4 rounded-[10px] border border-stroke bg-panel p-4 lg:p-6 xl:max-w-[600px] xl:flex-[1_1_480px]">{card}</section>
      <aside className="hidden min-w-0 xl:block xl:flex-[1_1_280px]">{aside}</aside>
    </div>
  );
}

/** The aside's head: a title, the figure that answers it, one muted line. */
export function AsideHead({ title, figure, line }: { title: string; figure: ReactNode; line?: ReactNode }) {
  return (
    <div className="space-y-2 pb-4">
      <h2 className="text-title font-semibold">{title}</h2>
      <div className="break-words font-mono tabular text-figure text-tg-hint">{figure}</div>
      {line && <p className="text-meta text-tg-hint">{line}</p>}
    </div>
  );
}

/** Desk and the time the balances on screen were read (Dubai clock) — `—` until a read answered; for About this job. */
export const deskFacts = (readAt: number | null) => [
  { label: 'Desk', value: 'Dubai' },
  { label: 'Read', value: readAt ? fmtTime(readAt) : '—' },
];

/** A plain asset select (Astra's), the folded codes it is given, shown in the desk's names. */
export function AssetSelect({ value, onChange, options, label }: {
  value: string;
  onChange: (a: string) => void;
  options: readonly string[];
  label: string;
}) {
  return (
    <select aria-label={label} className="input min-h-[44px] lg:min-h-[40px]" value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((a) => <option key={a} value={a}>{displayAsset(a)}</option>)}
    </select>
  );
}

/** The card's foot: a muted line at the left and the action(s) at the right; stacked on the phone, the button full width. */
export function FormFoot({ line, children }: { line?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-col-reverse gap-3 border-t border-stroke-soft pt-4 lg:flex-row lg:items-center lg:justify-between">
      {line ? <p className="text-center text-meta text-tg-hint lg:text-left">{line}</p> : <span />}
      <div className="flex shrink-0 flex-col gap-2 whitespace-nowrap lg:flex-row lg:items-center">{children}</div>
    </div>
  );
}

/** The review: what will post, read back before the one confirm. */
export function ReviewBlock({ children }: { children: ReactNode }) {
  return (
    <div className="space-y-2 rounded-lg border border-stroke bg-panel-2 p-3 lg:p-4">
      <h3 className="text-label font-medium uppercase tracking-wider text-tg-hint">Before confirming</h3>
      {children}
    </div>
  );
}
